import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { clearAuthStorage } from '@/services/api';
import * as authService from '@/services/authService';
import { AUTH_STORAGE_KEYS, AUTH_UNAUTHORIZED_EVENT } from '@/constants/auth';
import { parseApiError } from '@/utils/apiError';
import type {
  IAuthContextValue,
  IAuthProviderProps,
  ILoginResult,
  IRefreshSessionOptions,
  IUser,
} from '@/types/auth';

const AuthContext = createContext<IAuthContextValue | null>(null);

export function AuthProvider({ children }: IAuthProviderProps) {
  const [user, setUser] = useState<IUser | null>(() => {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
    if (!token) return null;
    try {
      const stored = localStorage.getItem(AUTH_STORAGE_KEYS.userData);
      return stored ? (JSON.parse(stored) as IUser) : { provisional: true };
    } catch {
      return { provisional: true };
    }
  });
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async (opts: IRefreshSessionOptions = { silent: false }): Promise<void> => {
    const token = localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }

    if (!opts.silent) setLoading(true);

    try {
      const data = await authService.fetchMe();
      localStorage.setItem(AUTH_STORAGE_KEYS.userData, JSON.stringify(data));
      setUser(data);
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 401 || status === 403) {
        clearAuthStorage();
        setUser(null);
        return;
      }
      console.error('Erro ao verificar sessão:', error);
      setUser((prev) => prev || { provisional: true });
    } finally {
      if (!opts.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void checkSession();
  }, [checkSession]);

  useEffect(() => {
    const onUnauthorized = () => {
      setUser(null);
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  const login = async (email: string, password: string): Promise<ILoginResult> => {
    try {
      const data = await authService.login({ email, senha: password });
      localStorage.setItem(AUTH_STORAGE_KEYS.accessToken, data.access_token);
      localStorage.setItem(AUTH_STORAGE_KEYS.refreshToken, data.refresh_token);
      await checkSession({ silent: true });
      return { ok: true };
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : 0;
      let message = parseApiError(error, 'Não foi possível realizar o login.');
      if (status === 401) {
        message = message || 'E-mail ou senha incorretos.';
        clearAuthStorage();
        setUser(null);
      }
      return { ok: false, status, message };
    }
  };

  const logout = async (): Promise<void> => {
    const refreshToken = localStorage.getItem(AUTH_STORAGE_KEYS.refreshToken);
    try {
      if (refreshToken) {
        await authService.logout({ refresh_token: refreshToken });
      }
    } catch (error) {
      if (!isAxiosError(error) || error.response?.status !== 401) {
        console.error('Erro ao fazer logout:', error);
      }
    } finally {
      clearAuthStorage();
      setUser(null);
    }
  };

  const isAuthenticated = !!user && !!localStorage.getItem(AUTH_STORAGE_KEYS.accessToken);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isAuthenticated,
        loading,
        refreshSession: checkSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): IAuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
}
